// frontend/src/components/admin/CondoUsersList.jsx
import React, { useEffect, useState } from "react";
import { API_URL } from "../../apiConfig";
import "./midias.css";

const Icon = ({ name, size = 18, stroke = 1.6, color = "currentColor" }) => {
  const paths = {
    plus:      <><path d="M12 5v14"/><path d="M5 12h14"/></>,
    refresh:   <><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10"/><path d="M20.49 15a9 9 0 01-14.85 3.36L1 14"/></>,
    edit:      <><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></>,
    trash:     <><path d="M3 6h18"/><path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/></>,
    search:    <><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></>,
    x:         <><path d="M18 6L6 18"/><path d="M6 6l12 12"/></>,
    check:     <path d="M20 6L9 17l-5-5"/>,
    arrowDown: <><path d="M12 5v14"/><path d="M19 12l-7 7-7-7"/></>,
    user:      <><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></>,
    building:  <><rect x="3" y="9" width="18" height="12"/><path d="M8 21V9"/><path d="M16 21V9"/><path d="M3 9l9-6 9 6"/></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" style={{ display: "block", flexShrink: 0 }}>
      {paths[name] || null}
    </svg>
  );
};

const Field = ({ label, required, hint, children }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
    <label style={{ fontSize: 11, fontWeight: 600, color: "var(--fg-2)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
      {label}{required && <span style={{ color: "var(--red)", marginLeft: 4 }}>*</span>}
    </label>
    {children}
    {hint && <div style={{ fontSize: 11, color: "var(--fg-3)", lineHeight: 1.4 }}>{hint}</div>}
  </div>
);

export default function CondoUsersList() {
  const [users, setUsers] = useState([]);
  const [condos, setCondos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortKey, setSortKey] = useState("email"); // email | condo | status | lastLogin
  const [sortDir, setSortDir] = useState("asc"); // asc | desc

  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [condoId, setCondoId] = useState("");
  const [condoSearch, setCondoSearch] = useState("");
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);

  const token = localStorage.getItem("token");

  useEffect(() => {
    loadUsers();
    loadCondos();
  }, []);

  async function loadUsers() {
    setLoading(true);
    setError("");
    try {
      const r = await fetch(`${API_URL}/admin/condo-users`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) {
        const data = await r.json();
        throw new Error(data.message || "Erro ao carregar usuários");
      }
      const data = await r.json();
      setUsers(data.users || []);
    } catch (e) {
      console.error(e);
      setError(e.message || "Erro ao carregar usuários");
    } finally {
      setLoading(false);
    }
  }

  async function loadCondos() {
    try {
      const r = await fetch(`${API_URL}/admin/screens`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) {
        const data = await r.json();
        const map = new Map();
        (data.screens || []).forEach((s) => {
          const id = s.superlogicaCondoId;
          if (id && !map.has(id)) {
            map.set(id, { id, name: s.superlogicaCondoName, city: s.superlogicaCondoCity || s.city, uf: s.superlogicaCondoUf || s.uf });
          }
        });
        setCondos(Array.from(map.values()).sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      }
    } catch (e) {
      console.error(e);
    }
  }

  function openCreate() {
    setEditingUser(null);
    setEmail("");
    setPassword("");
    setCondoId("");
    setCondoSearch("");
    setActive(true);
    setShowModal(true);
  }

  function openEdit(user) {
    setEditingUser(user);
    setEmail(user.email || "");
    setPassword("");
    setCondoId(user.condoId || "");
    setCondoSearch("");
    setActive(user.active);
    setShowModal(true);
  }

  async function handleSave() {
    if (!email.trim()) return alert("E-mail obrigatório");
    if (!condoId) return alert("Selecione o condomínio");
    if (!editingUser && password.length < 6) return alert("A senha deve ter ao menos 6 caracteres");

    setSaving(true);
    try {
      const condoName = condos.find((c) => c.id === condoId)?.name;
      const payload = { email, condoId, company: condoName, active };
      if (password) payload.password = password;

      const url = editingUser ? `${API_URL}/admin/condo-users/${editingUser._id}` : `${API_URL}/admin/condo-users`;
      const method = editingUser ? "PUT" : "POST";
      const r = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const data = await r.json();
        throw new Error(data.message || "Erro ao salvar usuário");
      }
      setShowModal(false);
      await loadUsers();
    } catch (e) {
      console.error(e);
      alert(e.message || "Erro ao salvar usuário");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    try {
      const r = await fetch(`${API_URL}/admin/condo-users/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) {
        const data = await r.json();
        throw new Error(data.message || "Erro ao desativar usuário");
      }
      setDeleteTarget(null);
      await loadUsers();
    } catch (e) {
      console.error(e);
      alert(e.message || "Erro ao desativar usuário");
    }
  }

  function formatDateTime(d) {
    if (!d) return "—";
    try {
      return new Date(d).toLocaleString("pt-BR", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    } catch { return "—"; }
  }

  const filtered = users.filter((u) => {
    if (!searchTerm.trim()) return true;
    const t = searchTerm.toLowerCase();
    return (u.email || "").toLowerCase().includes(t) || (u.condoName || "").toLowerCase().includes(t);
  });

  function toggleSort(key) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  function sortValue(u, key) {
    switch (key) {
      case "email": return (u.email || "").toLowerCase();
      case "condo": return (u.condoName || u.condoId || "").toLowerCase();
      case "status": return u.active ? 1 : 0;
      case "lastLogin": return u.lastLoginAt ? new Date(u.lastLoginAt).getTime() : 0;
      default: return "";
    }
  }

  const sortedUsers = [...filtered].sort((a, b) => {
    const va = sortValue(a, sortKey);
    const vb = sortValue(b, sortKey);
    let cmp = 0;
    if (typeof va === "number" && typeof vb === "number") cmp = va - vb;
    else cmp = String(va).localeCompare(String(vb), "pt-BR");
    return sortDir === "asc" ? cmp : -cmp;
  });

  const SortHeader = ({ label, sk, style }) => (
    <th
      onClick={() => toggleSort(sk)}
      style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap", ...style }}
      title="Clique para ordenar"
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        {label}
        <span style={{ opacity: sortKey === sk ? 1 : 0.25, fontSize: 10 }}>{sortKey === sk ? (sortDir === "asc" ? "▲" : "▼") : "▲"}</span>
      </span>
    </th>
  );

  const ativos = users.filter((u) => u.active).length;
  const filteredCondos = condoSearch
    ? condos.filter((c) => `${c.name} ${c.city || ""} ${c.uf || ""}`.toLowerCase().includes(condoSearch.toLowerCase()))
    : condos;

  return (
    <div className="midias-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Usuários do Condomínio</h1>
          <div className="page-sub">
            <span className="live-dot"/>
            <span><b style={{ color: "var(--fg)" }}>{ativos}</b> ativos · {users.length} no total</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn" onClick={loadUsers}><Icon name="refresh" size={14}/> Atualizar</button>
          <button className="btn btn-primary" onClick={openCreate}><Icon name="plus" size={14}/> Novo usuário</button>
        </div>
      </div>

      {error && (
        <div style={{ marginBottom: 14, padding: "12px 16px", background: "var(--red-soft)", border: "0.5px solid var(--red)", borderRadius: 10, color: "var(--red)", fontSize: 13 }}>{error}</div>
      )}

      <div className="card" style={{ marginTop: 4, padding: 16 }}>
        <div className="input-wrap" style={{ maxWidth: 360 }}>
          <label className="input-label">Buscar</label>
          <div className="input">
            <Icon name="search" size={14} color="var(--fg-3)"/>
            <input className="search-bare-input" type="text" placeholder="E-mail ou condomínio…" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}/>
            {searchTerm && <button onClick={() => setSearchTerm("")} style={{ border: "none", background: "transparent", display: "flex", color: "var(--fg-3)" }}><Icon name="x" size={14}/></button>}
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 14, padding: "16px 0 8px" }}>
        <div className="card-h" style={{ padding: "0 20px 14px" }}>
          <h3>Usuários <span className="meta" style={{ marginLeft: 8 }}>{filtered.length} de {users.length}</span></h3>
        </div>
        {loading ? (
          <div style={{ padding: "60px 20px", textAlign: "center", color: "var(--fg-3)" }}>Carregando…</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="tbl">
              <thead>
                <tr>
                  <SortHeader label="E-mail" sk="email" style={{ paddingLeft: 20 }} />
                  <SortHeader label="Condomínio" sk="condo" />
                  <SortHeader label="Status" sk="status" />
                  <SortHeader label="Último acesso" sk="lastLogin" />
                  <th style={{ width: 120, paddingRight: 20 }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map((u) => (
                  <tr key={u._id}>
                    <td style={{ paddingLeft: 20 }}><span style={{ fontWeight: 600 }}>{u.email}</span></td>
                    <td><span style={{ color: "var(--fg-2)", fontSize: 13 }}>{u.condoName || u.condoId || "—"}</span></td>
                    <td><span className={`badge ${u.active ? "badge-green" : "badge-gray"}`}><span className="badge-dot"/>{u.active ? "Ativo" : "Desativado"}</span></td>
                    <td className="num" style={{ color: "var(--fg-2)", fontSize: 12 }}>{formatDateTime(u.lastLoginAt)}</td>
                    <td style={{ paddingRight: 20 }}>
                      <div className="midia-actions">
                        <button className="midia-act purple" title="Editar" onClick={() => openEdit(u)}><Icon name="edit" size={14}/></button>
                        {u.active && <button className="midia-act red" title="Desativar" onClick={() => setDeleteTarget(u)}><Icon name="trash" size={14}/></button>}
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ padding: "60px 20px", textAlign: "center", color: "var(--fg-3)" }}>
                      <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                        <Icon name="user" size={24} color="var(--fg-4)"/>
                        <div>Nenhum usuário do condomínio cadastrado</div>
                        <button className="btn btn-primary" style={{ marginTop: 8 }} onClick={openCreate}><Icon name="plus" size={13}/> Criar usuário</button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ height: 24 }}/>

      {/* Modal criar/editar */}
      {showModal && (
        <div onClick={() => setShowModal(false)} style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(0,0,0,0.32)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: "min(480px, 100%)", maxHeight: "calc(100vh - 40px)", background: "var(--surface-strong)", backdropFilter: "blur(40px) saturate(180%)", border: "0.5px solid var(--hairline-strong)", borderRadius: 18, display: "flex", flexDirection: "column", boxShadow: "var(--shadow-md)", overflow: "hidden" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 22px", borderBottom: "0.5px solid var(--hairline)" }}>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600 }}>{editingUser ? "Editar usuário" : "Novo usuário do condomínio"}</h2>
              <button className="icon-btn" onClick={() => setShowModal(false)}><Icon name="x" size={16}/></button>
            </div>
            <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16, overflowY: "auto", flex: 1, minHeight: 0 }}>
              <Field label="E-mail" required>
                <div className="input">
                  <input className="search-bare-input" type="email" placeholder="sindico@condominio.com" value={email} onChange={(e) => setEmail(e.target.value)} disabled={saving}/>
                </div>
              </Field>
              <Field label={editingUser ? "Nova senha (opcional)" : "Senha"} required={!editingUser} hint={editingUser ? "Deixe em branco para manter a senha atual." : "Mínimo 6 caracteres."}>
                <div className="input">
                  <input className="search-bare-input" type="text" placeholder={editingUser ? "••••••••" : "Defina uma senha"} value={password} onChange={(e) => setPassword(e.target.value)} disabled={saving} autoComplete="new-password"/>
                </div>
              </Field>
              <Field label="Condomínio" required hint={condoId ? condos.find((c) => c.id === condoId)?.name : "Selecione a qual condomínio este usuário pertence."}>
                <div className="input" style={{ marginBottom: 8 }}>
                  <Icon name="search" size={13} color="var(--fg-3)"/>
                  <input className="search-bare-input" type="text" placeholder="Filtrar condomínios…" value={condoSearch} onChange={(e) => setCondoSearch(e.target.value)} disabled={saving}/>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 200, overflowY: "auto", border: "0.5px solid var(--hairline)", borderRadius: 10, padding: 4 }}>
                  {filteredCondos.length === 0 ? (
                    <div style={{ padding: 16, textAlign: "center", color: "var(--fg-3)", fontSize: 12 }}>Nenhum condomínio encontrado</div>
                  ) : filteredCondos.map((c) => {
                    const on = condoId === c.id;
                    return (
                      <button key={c.id} onClick={() => setCondoId(c.id)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: on ? "var(--accent-soft)" : "transparent", border: "0.5px solid " + (on ? "color-mix(in oklch, var(--accent) 30%, transparent)" : "transparent"), borderRadius: 8, font: "inherit", color: "inherit", textAlign: "left", cursor: "default" }}>
                        <span style={{ width: 22, height: 22, borderRadius: 6, background: "var(--accent-soft)", color: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon name="building" size={12}/></span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: on ? 600 : 500 }}>{c.name}</div>
                          {(c.city || c.uf) && <div style={{ fontSize: 11, color: "var(--fg-3)" }}>{[c.city, c.uf].filter(Boolean).join("-")}</div>}
                        </div>
                        {on && <Icon name="check" size={14} color="var(--accent)"/>}
                      </button>
                    );
                  })}
                </div>
              </Field>
              <label style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: "var(--surface)", border: "0.5px solid var(--hairline)", borderRadius: 10, cursor: "default" }}>
                <div onClick={() => !saving && setActive(!active)} style={{ width: 36, height: 20, borderRadius: 999, background: active ? "var(--accent)" : "var(--surface-sunken)", border: "0.5px solid var(--hairline)", position: "relative", flexShrink: 0 }}>
                  <div style={{ position: "absolute", top: 2, left: active ? 18 : 2, width: 16, height: 16, borderRadius: "50%", background: "white", boxShadow: "0 1px 3px rgba(0,0,0,0.2)", transition: "left .15s ease" }}/>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500, fontSize: 13 }}>Usuário ativo</div>
                  <div style={{ fontSize: 11, color: "var(--fg-3)" }}>{active ? "Pode acessar o portal do condomínio" : "Acesso bloqueado"}</div>
                </div>
              </label>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "14px 22px", borderTop: "0.5px solid var(--hairline)", background: "var(--surface)" }}>
              <button className="btn" onClick={() => setShowModal(false)} disabled={saving}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving || !email.trim() || !condoId || (!editingUser && password.length < 6)}>
                {saving ? "Salvando…" : <><Icon name="check" size={14}/> {editingUser ? "Salvar" : "Criar usuário"}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmar desativação */}
      {deleteTarget && (
        <div onClick={() => setDeleteTarget(null)} style={{ position: "fixed", inset: 0, zIndex: 400, background: "rgba(0,0,0,0.32)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: "min(420px, 100%)", background: "var(--surface)", border: "0.5px solid var(--hairline-strong)", borderRadius: 18, padding: 24, boxShadow: "var(--shadow-md)" }}>
            <h3 style={{ margin: "0 0 8px", fontSize: 16, fontWeight: 600 }}>Desativar usuário</h3>
            <p style={{ fontSize: 13, color: "var(--fg-2)", marginBottom: 20 }}>Desativar o acesso de <strong>{deleteTarget.email}</strong>? Os comunicados criados por ele continuarão existindo.</p>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn" onClick={() => setDeleteTarget(null)}>Cancelar</button>
              <button className="btn" onClick={() => handleDelete(deleteTarget._id)} style={{ background: "var(--red-soft)", color: "var(--red)", borderColor: "transparent" }}><Icon name="trash" size={13}/> Desativar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
